import { Injectable, inject, signal, computed, Signal, WritableSignal } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus, EventType, EventMessage, AccountInfo } from '@azure/msal-browser';
import { tap, catchError, of } from 'rxjs';
import { GraphService } from './graph.service';
import { environment } from '../../../environments/environment';
import { TokenClaims } from '@monorepo/interfaces';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private msalService = inject(MsalService);
    private msalBroadcastService = inject(MsalBroadcastService);
    private graphService = inject(GraphService);

    private _isAuthenticated: WritableSignal<boolean> = signal<boolean>(false);
    private _isAuthLoading: WritableSignal<boolean> = signal<boolean>(true);
    private _userAccount: WritableSignal<AccountInfo | null> = signal<AccountInfo | null>(null);
    private _userPhoto: WritableSignal<Blob | null> = signal<Blob | null>(null);
    private _isPhotoLoading: WritableSignal<boolean> = signal<boolean>(false);
    private _photoError: WritableSignal<string | null> = signal<string | null>(null);

    public isAuthenticated: Signal<boolean> = computed(() => this._isAuthenticated());
    public isAuthLoading: Signal<boolean> = computed(() => this._isAuthLoading());
    public userIdTokenClaims: Signal<TokenClaims | null> = computed(
        () => this._userAccount()?.idTokenClaims as TokenClaims
    );
    public userName: Signal<string | null> = computed(
        () => `${this.userIdTokenClaims()?.preferred_family_name}, ${this.userIdTokenClaims()?.preferred_given_name}`
    );

    constructor() {
        this.initializeAuth();
    }

    public logout(): void {
        this._userPhoto.set(null);
        this.msalService.logoutRedirect();
    }

    public getUserPhotoUrl(): string | null {
        const photo = this._userPhoto();
        return photo ? URL.createObjectURL(photo) : null;
    }

    private initializeAuth(): void {
        this.subscribeToLoginAndLogout();
        this.subscribeToAuthenticationDone();
    }

    private subscribeToAuthenticationDone() {
        this.msalBroadcastService.inProgress$
            .pipe(
                tap((status: InteractionStatus) => {
                    this._isAuthLoading.set(status !== InteractionStatus.None);

                    if (status === InteractionStatus.None) {
                        this.updateAuthState();
                    }
                })
            )
            .subscribe();
    }

    private subscribeToLoginAndLogout() {
        this.msalBroadcastService.msalSubject$
            .pipe(
                tap((msg: EventMessage) => {
                    if (msg.eventType === EventType.LOGOUT_SUCCESS) {
                        this._isAuthenticated.set(false);
                        this._userAccount.set(null);
                        this._userPhoto.set(null);
                    }
                })
            )
            .subscribe();
    }

    private updateAuthState(): void {
        const accounts = this.msalService.instance.getAllAccounts();

        if (accounts.length > 0) {
            const activeAccount = this.msalService.instance.getActiveAccount() || accounts[0];

            if (activeAccount && activeAccount.idTokenClaims?.roles?.length) {
                this.msalService.instance.setActiveAccount(activeAccount);
                this._userAccount.set(activeAccount);
                this._isAuthenticated.set(true);

                // Load user photo after successful authentication
                this.loadUserPhoto();
            } else {
                this.setUnauthorizedUserSignals();
            }
        } else {
            this.setUnauthorizedUserSignals();
        }

        this._isAuthLoading.set(false);
    }

    private loadUserPhoto(): void {
        this._isPhotoLoading.set(true);
        this._photoError.set(null);

        this.graphService
            .getPhotoForUser(this.userIdTokenClaims()?.oid ?? 'me', environment.apiConfig[1].scopes)
            .pipe(
                tap(photo => {
                    this._userPhoto.set(photo);
                    this._isPhotoLoading.set(false);
                }),
                catchError(error => {
                    this._photoError.set('Failed to load user photo');
                    this._isPhotoLoading.set(false);
                    console.error('Error loading user photo:', error);
                    return of(null);
                })
            )
            .subscribe();
    }

    private setUnauthorizedUserSignals() {
        this._isAuthenticated.set(false);
        this._userAccount.set(null);
        this._userPhoto.set(null);
    }
}
